import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  Building,
  Building2,
  Users,
  Webhook,
  ScrollText,
  KeyRound,
  Settings,
  ArrowLeftRight,
  ChevronLeft,
  Check,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";

const navItems = [
  { to: "/", label: "Overview", icon: Home },
  { to: "/organisations", label: "Organisations", icon: Building2 },
  { to: "/companies", label: "Companies", icon: Building },
  { to: "/users", label: "Members", icon: Users },
  { to: "/webhooks", label: "Webhooks", icon: Webhook },
  { to: "/api-keys", label: "API Keys", icon: KeyRound },
  { to: "/logs", label: "Logs", icon: ScrollText },
  { to: "/settings", label: "Settings", icon: Settings },
];

type Page = "root" | "clients";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user, activeClient, activeClientId, switchClient } = useAuth();
  const clients = user?.clients ?? [];
  const inputRef = useRef<HTMLInputElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [page, setPage] = useState<Page>("root");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setPage("root");
      setSearch("");
    }
  }, [open]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [page]);

  const close = () => onOpenChange(false);

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Command menu"
      description="Search pages or switch your active client."
      onOpenAutoFocus={() => {
        returnFocus.current = document.activeElement as HTMLElement | null;
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        if (returnFocus.current?.isConnected) returnFocus.current.focus();
      }}
    >
      <CommandInput
        ref={inputRef}
        aria-label="Search pages or switch client"
        placeholder={
          page === "clients" ? "Search clients…" : "Type a command or search…"
        }
        value={search}
        onValueChange={setSearch}
        onKeyDown={(e) => {
          if (e.key === "Backspace" && search === "" && page !== "root") {
            e.preventDefault();
            setPage("root");
          }
        }}
      />
      <CommandList className="command-results max-h-[min(55dvh,520px)]">
        <CommandEmpty>No results found.</CommandEmpty>

        {page === "root" && (
          <>
            <CommandGroup heading="Client">
              <CommandItem
              animated
                value="switch client"
                onSelect={() => {
                  setSearch("");
                  setPage("clients");
                }}
              >
                <ArrowLeftRight />
                <span>Switch Client</span>
                <span className="ml-auto truncate text-xs text-muted-foreground">
                  {activeClient?.clientName ?? "-"}
                </span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Navigation">
              {navItems.map((it) => (
                <CommandItem
                  animated
                  key={it.to}
                  value={it.label}
                  onSelect={() => {
                    navigate(it.to);
                    close();
                  }}
                >
                  <it.icon />
                  <span>{it.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {page === "clients" && (
          <CommandGroup heading="Switch client">
            <CommandItem
              animated
              value="__back"
              onSelect={() => {
                setSearch("");
                setPage("root");
              }}
            >
              <ChevronLeft />
              <span>Back</span>
            </CommandItem>
            {clients.map((c) => {
              const active = c.clientId === activeClientId;
              return (
                <CommandItem
                  animated
                  key={c.clientId}
                  value={`${c.clientName} ${c.type} ${c.role}`}
                  onSelect={() => {
                    switchClient(c.clientId);
                    close();
                  }}
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">
                      {c.clientName}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {c.role}
                    </span>
                  </div>
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                      c.type === "Sandbox"
                        ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                        : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
                    )}
                  >
                    {c.type}
                  </span>
                  {active && <Check className="text-foreground" />}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
      <div className="flex items-center justify-between gap-3 border-t bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
        <span>{page === "clients" ? "Choose your workspace" : "Your workspace, one shortcut away"}</span>
        <span className="hidden sm:inline">↑ ↓ navigate · ↵ select</span>
      </div>
    </CommandDialog>
  );
}
